// Team tab of the advertiser Settings surface.
//
// Extracted verbatim from the "Invite a teammate", "On the team", and
// "Pending invitations" sections of the former MyTeamPage. Preserves the
// same roster fetch effect, invite handler (with analytics event), and
// revoke handler.

import React from 'react';
import { useParams } from 'react-router-dom';
import {
  UserPlus,
  Users,
  Trash2,
  Loader2,
  AlertTriangle,
  Building2,
} from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { usePublisherConfig } from '../../data/publisherConfigs';
import { apiClient, ApiError } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import PrimaryButton from '../../components/self-service/shared/PrimaryButton';
import Banner from '../../components/self-service/shared/Banner';

interface MemberRow {
  membershipId: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'revoked';
  invitedAt: string;
  acceptedAt: string | null;
  expiresAt: string | null;
  userId: string | null;
  invitedBy: string | null;
}

const SettingsTeamTab: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { config } = usePublisherConfig(slug);
  const { user, selectedAdvertiser, advertiserMemberships } = useAuth();

  const accent = config?.accentColor || config?.primaryColor;
  const primary = config?.primaryColor;

  const [members, setMembers] = React.useState<MemberRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [listError, setListError] = React.useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviting, setInviting] = React.useState(false);
  const [inviteError, setInviteError] = React.useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = React.useState<string | null>(null);

  const fetchMembers = React.useCallback(async (advertiserId: string) => {
    setLoading(true);
    setListError(null);
    try {
      const data = await apiClient.get<{ members: MemberRow[] }>(
        `/advertisers/${advertiserId}/memberships`,
      );
      setMembers(data.members ?? []);
    } catch (e) {
      setListError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to load team.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (selectedAdvertiser?.advertiserId) {
      void fetchMembers(selectedAdvertiser.advertiserId);
    } else {
      setMembers([]);
      setLoading(false);
    }
  }, [selectedAdvertiser?.advertiserId, fetchMembers]);

  if (advertiserMemberships !== null && advertiserMemberships.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center">
        <Building2 className="w-10 h-10 text-slate-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-slate-900 mb-2">No business yet</h2>
        <p className="text-sm text-slate-600">
          Submit a campaign first to set up an advertiser, then come back to invite teammates.
        </p>
      </div>
    );
  }

  const acceptedMembers = members.filter((m) => m.status === 'accepted');
  const pendingInvites = members.filter((m) => m.status === 'pending');

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAdvertiser) return;
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    setInviteError(null);
    setInviteSuccess(null);
    try {
      const resp = await apiClient.post<{
        id: string;
        token: string | null;
        alreadyMember: boolean;
      }>(
        `/advertisers/${selectedAdvertiser.advertiserId}/invitations`,
        // stationSlug is a routing hint stored on the new membership row so
        // the post-accept redirect lands the invitee inside this same closed
        // loop. It does NOT scope membership permissions — see CTV-427.
        { email, stationSlug: slug },
      );
      if (resp.alreadyMember) {
        setInviteSuccess(`${email} is already on this team.`);
      } else {
        analytics.track(EVENTS.TEAM_MEMBER_INVITED, {
          station_slug: slug,
          advertiser_id: selectedAdvertiser.advertiserId,
        });
        setInviteSuccess(`Invitation sent to ${email}.`);
      }
      setInviteEmail('');
      await fetchMembers(selectedAdvertiser.advertiserId);
    } catch (e) {
      setInviteError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to send invite.',
      );
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (membershipId: string, label: string) => {
    if (!selectedAdvertiser) return;
    const confirmed = window.confirm(`Remove ${label}?`);
    if (!confirmed) return;
    try {
      await apiClient.post(`/memberships/${membershipId}/revoke`);
      analytics.track(EVENTS.TEAM_MEMBER_REVOKED, {
        station_slug: slug,
        advertiser_id: selectedAdvertiser.advertiserId,
      });
      await fetchMembers(selectedAdvertiser.advertiserId);
    } catch (e) {
      window.alert(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to remove.',
      );
    }
  };

  return (
    <div className="space-y-6">
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900">Invite a teammate</h2>
        </div>
        <form
          onSubmit={handleInvite}
          className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-2"
        >
          <input
            type="email"
            required
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="teammate@example.com"
            className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
          />
          <PrimaryButton
            type="submit"
            disabled={inviting || !inviteEmail.trim()}
            accentColor={accent}
            primaryColor={primary}
          >
            {inviting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {inviting ? 'Sending…' : 'Send invite'}
          </PrimaryButton>
        </form>
        {inviteSuccess && (
          <div className="px-5 pb-4">
            <Banner tone="success" label="Invite sent">
              {inviteSuccess}
            </Banner>
          </div>
        )}
        {inviteError && (
          <div className="px-5 pb-4">
            <Banner tone="warn" label="Couldn't send">
              {inviteError}
            </Banner>
          </div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900">On the team</h2>
        </div>
        {loading ? (
          <div className="px-5 py-4 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : listError ? (
          <div className="px-5 py-4">
            <Banner
              tone="warn"
              label="Couldn't load team"
              icon={<AlertTriangle className="w-3 h-3" />}
            >
              {listError}
            </Banner>
          </div>
        ) : acceptedMembers.length === 0 ? (
          <div className="px-5 py-4 text-sm text-slate-500">No accepted members yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {acceptedMembers.map((m) => {
              const isSelf = m.userId === user?.id;
              return (
                <li
                  key={m.membershipId}
                  className="px-5 py-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{m.email}</div>
                    <div className="text-xs text-slate-500">
                      Joined {m.acceptedAt ? new Date(m.acceptedAt).toLocaleDateString() : '—'}
                      {isSelf && ' · You'}
                    </div>
                  </div>
                  {!isSelf && (
                    <button
                      type="button"
                      onClick={() => handleRevoke(m.membershipId, m.email)}
                      title="Remove from team"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                      Remove
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900">Pending invitations</h2>
        </div>
        {loading ? (
          <div className="px-5 py-4 text-sm text-slate-500">Loading…</div>
        ) : pendingInvites.length === 0 ? (
          <div className="px-5 py-4 text-sm text-slate-500">No pending invitations.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {pendingInvites.map((m) => (
              <li
                key={m.membershipId}
                className="px-5 py-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{m.email}</div>
                  <div className="text-xs text-slate-500">
                    Invited {new Date(m.invitedAt).toLocaleDateString()}
                    {m.expiresAt && ` · Expires ${new Date(m.expiresAt).toLocaleDateString()}`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRevoke(m.membershipId, m.email)}
                  title="Revoke invitation"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 transition-all"
                >
                  <Trash2 className="w-3 h-3" />
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default SettingsTeamTab;
